import React from 'react';
import PropTypes from 'prop-types';
import { find, sortBy } from 'lodash';
import { Dropdown } from 'semantic-ui-react';
import request from 'utils/request';
import reportError from 'utils/report-error';
import { SFEntityID } from 'utils/types';
import argTypes from 'utils/arg-types';
import css from './CohortDropdown.scss';
import Icon from './MaterialIcon';

const rawCohortTypes = argTypes('rawCohort', PropTypes.arrayOf(PropTypes.shape({
  type: PropTypes.shape({
    id: SFEntityID.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,

  id: SFEntityID.isRequired,
  name: PropTypes.string.isRequired,
})));

const groupCohortsByType = rawCohortTypes(rawCohorts => {
  const cohortsByType = sortBy(rawCohorts, cohort => cohort.type.name);

   const cohorts = cohortsByType.map(cohort => {
    return {
      text: cohort.name,
      content: <div><b>{cohort.name}</b><br/>{cohort.type.name}</div>,
      value: cohort.id
    };
  });

  // keyboard only/screenreader users need a way to deselect cohorts
  // until we can make the multiselect dropdown fully accessible
  cohorts.unshift({
    text: 'None',
    content: <div><b>None</b></div>,
    value: -1
  });

  return cohorts;
});

const fetchCohorts = () => {
  return request.fetchAuthenticatedStarfishJSON('../instructor/getCohortTemplates.html', 'cohortTemplates', {
    method: 'POST',
    body: request.urlParams({
      start: 0,
      limit: -1
    })
  }).then(groupCohortsByType);
};

export class CohortDropdown extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      cohorts: [],
      // IE needs access to the value as a prop when we use onBlur so we need to keep
      // track of the value in the state
      chosenCohortIds: this.props.defaultCohortIds,
      cohortLimitReached: false
    };
  }

  /**
   * Updates the selected cohorts both through the state of this component and to the parent component through
   * the onChange prop.
   * @param {Array.<number>} cohortIds the new list of cohort IDs selected
   */
  updateCohorts = (cohortIds) => {
    this.setState({cohortLimitReached: false, chosenCohortIds: cohortIds});
    this.props.onChange && this.props.onChange(cohortIds)
  }

  /**
   * Takes the event from the dropdown activity, and either clears the list or updates it. If the prop
   * maxCohortCount is set, this will also ensure that limit is enforced and display an error if
   * the update would break it.
   *
   * @param {Array.<number>} cohortIds the new list of cohort IDs selected
   */
  onConnectionChange = (cohortIds) => {
    const clearSelection = find(cohortIds, (chosenCohort) => {return chosenCohort === -1});
    if (!clearSelection && this.props.maxCohortCount && cohortIds.length > this.props.maxCohortCount) {
      this.setState({cohortLimitReached: true});
      return;
    }
    this.updateCohorts(clearSelection ? [] : cohortIds);
  }

  componentDidMount() {
    fetchCohorts().then((cohorts) => this.setState({ cohorts })).catch(reportError);
  }

  // use onBlur instead of onChange to avoid calling the db every time a cohort is selected or removed
  render() {
    return <>
      {this.state.cohortLimitReached && <span className={css['error']}>
        <Icon icon="error" aria-hidden="true" /> You have reached the limit of {this.props.maxCohortCount} cohorts.
      </span>}
      <Dropdown selection multiple
                id={this.props.id}
                options={this.state.cohorts}
                value={this.state.chosenCohortIds}
                onBlur={(e, selectedCohorts) => this.props.onBlur(selectedCohorts.value)}
                onChange={(e, selectedCohorts) => this.onConnectionChange(selectedCohorts.value)}
                disabled={this.props.disabled}
      />
    </>
  }
}

CohortDropdown.defaultProps = {
  maxCohortCount: null,
  disabled: false
}

CohortDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  defaultCohortIds: PropTypes.arrayOf(PropTypes.number),
  maxCohortCount: PropTypes.number,
  disabled: PropTypes.bool
}
